<template>
  <div class="relative">
    <div
      v-if="showRawMessages"
      class="mb-2"
    >
      <div class="rounded-lg bg-gray-900 p-4">
        <VueJsonPretty
          :data="message"
          :deep="2"
          :show-double-quotes="false"
          :show-length="true"
          class="!bg-transparent"
          :showLine="false"
          :collapsed-on-click-brackets="true"
          :path-selectable="true"
        />
      </div>
    </div>
    <div
      :class="
        isAiAssistantAppRoute
          ? 'flex w-full flex-col items-start gap-3 self-stretch'
          : 'flex gap-x-2 sm:gap-x-4'
      "
    >
      <!-- AI Avatar (fullscreen KI-Assistent only: hidden — legacy global/case match origin/master) -->
      <div
        v-if="!isAiAssistantAppRoute"
        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100"
      >
        <span class="text-xs font-medium text-purple-600">AI</span>
      </div>
      <div
        :data-cy="isAiAssistantAppRoute ? 'ai-message' : undefined"
        :class="
          isAiAssistantAppRoute
            ? 'w-full max-w-2xl space-y-3'
            : 'w-full max-w-[90%] grow space-y-3 md:max-w-2xl'
        "
      >
        <ChatStreamingLoader v-if="isAiAssistantAppRoute && showAssistantThinkingWave" />
        <div
          v-show="!isAiAssistantAppRoute || !showAssistantThinkingWave"
          :class="
            isAiAssistantAppRoute
              ? 'space-y-3'
              : 'space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900'
          "
          :data-cy="!isAiAssistantAppRoute ? 'ai-message' : undefined"
        >
          <!-- Thinking process (Denkprozess).
               Shown only after streaming completes: the thinking field arrives in the final
               AI_CHAT_MESSAGE_COMPLETE payload, not mid-stream. To show CoT live during streaming,
               the stream parser would need to extract thinking separately — that's a follow-up. -->
          <ThinkingProcessSection
            v-if="thinkingContent && !isStreaming"
            :thinking="thinkingContent"
          />

          <!-- Verdict badge -->
          <div
            v-if="verdictChunks.length > 0"
            class="flex w-fit items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-0.5"
          >
            <Scale class="h-3.5 w-3.5 shrink-0 text-gray-600" />
            <span class="text-xs font-semibold text-gray-600">
              {{ t('SupportedByVerifiedVerdicts') }}
            </span>
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            ref="proseContent"
            class="prose prose-sm max-w-none overflow-auto text-sm text-gray-800 dark:prose-invert prose-p:my-1
              prose-pre:border prose-pre:border-neutral-200 prose-pre:bg-neutral-100 prose-ol:my-0.5
              prose-ol:list-decimal prose-ol:pl-5 prose-ul:my-0.5 prose-ul:list-disc prose-ul:pl-5 prose-li:my-0.5
              prose-li:pl-2 prose-li:marker:text-gray-400 dark:text-white dark:prose-pre:border-neutral-700
              dark:prose-pre:bg-neutral-800 dark:prose-li:marker:text-gray-500 [&_a:hover]:text-purple-700
              [&_a]:text-purple-600 [&_a]:no-underline"
            v-html="renderedContent"
          />

          <!-- Verdict list -->
          <VerdictList
            :verdicts="verdictChunks"
            :pdf-error-uuids="pdfErrorUuids"
            @open-pdf="openVerdictPdf"
            @download-pdf="downloadVerdictPdf"
          />
        </div>

        <!-- Verdict retrieval error notice -->
        <div
          v-if="message.verdict_retrieval_error"
          class="flex max-w-2xl items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm
            text-amber-800"
        >
          <AlertTriangle class="mt-0.5 h-4 w-4 shrink-0" />
          <span>{{ t('VerdictRetrievalError') }}</span>
        </div>

        <!-- Verdict activation hint -->
        <VerdictActivationHint
          v-if="verdictHintVisible"
          @enable="enableCourtDb"
          @reask="handleReask"
          @close="verdictHintVisible = false"
        />
        <!-- AI Message Toolbar (hidden during streaming) -->
        <AiMessageToolbar
          v-if="!isStreaming"
          :message="message"
          @save-snippet="openSaveSnippetDialog"
          @rated="onRated"
        />
      </div>
    </div>

    <!-- Feedback Dialog -->
    <div
      v-if="showFeedbackDialog"
      ref="feedbackDialogRef"
      class="mt-4 max-w-2xl rounded-lg bg-purple-200/50 p-3"
    >
      <div class="mb-3 flex items-start justify-between">
        <div class="flex items-center gap-2">
          <CheckIcon class="h-4 w-4 text-purple-600" />
          <span class="text-sm text-purple-600">
            {{ t('RatingSaved') }}
          </span>
        </div>
        <button
          class="text-purple-400 hover:text-purple-600"
          @click="closeFeedbackDialog"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div>

      <div class="rounded-lg bg-white p-4">
        <h3 class="mb-2 text-sm font-semibold text-gray-900">
          {{ rating === 'positive' ? t('WhatDidYouLike') : t('WhatDidntYouLike') }}
        </h3>
        <p class="mb-4 text-sm text-gray-600">
          {{
            rating === 'positive' ? t('FeedbackHelpsImprove') : t('FeedbackHelpsImproveNegative')
          }}
        </p>

        <!-- Issue selection for negative feedback -->
        <div
          v-if="rating === 'negative'"
          class="mb-4"
        >
          <select
            v-model="selectedIssue"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent
              focus:outline-none focus:ring-2 focus:ring-purple-500"
            :placeholder="t('SelectIssue')"
          >
            <option
              value=""
              disabled
            >
              {{ t('SelectIssue') }}
            </option>
            <option value="incorrect_information">
              {{ t('IncorrectOrOutdatedInformation') }}
            </option>
            <option value="incomplete_response">
              {{ t('IncompleteResponse') }}
            </option>
            <option value="did_not_follow_instructions">
              {{ t('DidNotFollowInstructions') }}
            </option>
            <option value="inappropriate_style">
              {{ t('InappropriateStyleOrFormatting') }}
            </option>
            <option value="other">
              {{ t('Other') }}
            </option>
          </select>
        </div>

        <div class="mb-4">
          <textarea
            v-model="feedbackComment"
            rows="4"
            class="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent
              focus:outline-none focus:ring-2 focus:ring-purple-500"
            :placeholder="rating === 'positive' ? t('YourFeedback') : t('HowCouldThisBeImproved')"
          ></textarea>
        </div>

        <div class="flex justify-end">
          <button
            :disabled="!feedbackComment.trim() || (rating === 'negative' && !selectedIssue)"
            class="rounded bg-purple-600 px-4 py-1.5 text-sm text-white transition-colors hover:bg-purple-700
              disabled:opacity-50"
            @click="submitFeedback"
          >
            {{ t('SendFeedback') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Preference Suggestion -->
    <PreferenceSuggestion
      v-if="message.preference_suggestion"
      ref="preferenceSuggestionRef"
      class="mt-4"
      :suggestion="message.preference_suggestion"
      :session-type="sessionType"
      @dismissed="handleSuggestionDismissed"
      @saved="handleSuggestionSaved"
      @view-in-library="handleViewInLibrary"
    />
  </div>

  <!-- Inline verdict citation floating popover -->
  <VerdictCitationPopover
    :hovered-citation="hoveredCitation"
    :popover-style="citationPopoverStyle"
    @keep-open="keepCitationOpen"
    @schedule-hide="scheduleCitationHide"
    @open-pdf="openVerdictPdf"
  />

  <!-- Save Response Dialog -->
  <Teleport to="body">
    <SaveSnippetDialog
      v-if="showSaveSnippetDialog"
      :content="getMessageContent()"
      :case-uuid="caseStore.caseRecord?.uuid"
      @close="showSaveSnippetDialog = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useVerdictCitationPopover } from '@/composables/useVerdictCitationPopover'
  import { useVerdictPdfActions } from '@/composables/useVerdictPdfActions'
  import { useVerdictChunks, type VerdictChunk } from '@/composables/useVerdictChunks'
  import { useVerdictHint } from '@/composables/useVerdictHint'
  import VerdictList from './VerdictList.vue'
  import VerdictCitationPopover from './VerdictCitationPopover.vue'
  import { type AIChatSessionMessage, AIChatSessionTypeEnum } from '@/types/generated'
  import {
    type DocumentRegistryEntry,
    escapeHtml,
    renderDocumentBadge,
    rewriteDocumentAnchorsAsBadges
  } from '@/utils/chatDocumentBadge'
  import { FeedbackService } from '@/types/generated/services.gen'
  import { CheckIcon } from '@heroicons/vue/24/outline'
  import { AlertTriangle, Scale, XIcon } from 'lucide-vue-next'
  import DOMPurify from 'dompurify'
  import MarkdownIt from 'markdown-it'
  import {
    computed,
    inject,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    toRef,
    type Ref,
    watch
  } from 'vue'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import AiMessageToolbar from './AiMessageToolbar.vue'
  import PreferenceSuggestion from './PreferenceSuggestion.vue'
  import SaveSnippetDialog from './SaveSnippetDialog.vue'
  import ThinkingProcessSection from './ThinkingProcessSection.vue'
  import VerdictActivationHint from './VerdictActivationHint.vue'
  import ChatStreamingLoader from '@/components/app/chat/messages/ChatStreamingLoader.vue'
  import { useIsAiAssistantAppRoute } from '@/composables/useIsAiAssistantAppRoute'

  const props = defineProps<{
    message: AIChatSessionMessage & { _isStreaming?: boolean }
    isLatestMessage?: boolean
  }>()

  const isAiAssistantAppRoute = useIsAiAssistantAppRoute()

  const emit = defineEmits<{
    openPreferencesDialog: []
    'document-click': [document: any]
    'update-message': [message: AIChatSessionMessage]
  }>()

  const { t } = useTranslation({
    en: {
      Page: 'Page',
      Pages: 'Pages',
      CopyToClipboard: 'Copy to clipboard',
      Copy: 'Copy',
      Copied: 'Copied!',
      Helpful: 'Mark as helpful',
      NotHelpful: 'Mark as not helpful',
      MarkedHelpful: 'Marked as helpful',
      MarkedNotHelpful: 'Marked as not helpful',
      SaveResponse: 'Save snippet',
      ResponseSaved: 'Snippet saved',
      Saved: 'Saved',
      RatingSaved: 'Would you like to provide additional feedback?',
      WhatDidYouLike: 'What did you like about the response?',
      WhatDidntYouLike: "What didn't you like about the response?",
      FeedbackHelpsImprove: 'Your feedback helps us improve the quality of the AI responses',
      FeedbackHelpsImproveNegative:
        'Your feedback helps us understand what went wrong and improve future responses',
      YourFeedback: 'Your feedback',
      HowCouldThisBeImproved: 'How could this response be improved?',
      SendFeedback: 'Send feedback',
      SelectIssue: 'Select the issue you encountered',
      IncorrectOrOutdatedInformation: 'Incorrect or outdated information',
      IncompleteResponse: 'Incomplete response',
      DidNotFollowInstructions: 'Did not follow instructions',
      InappropriateStyleOrFormatting: 'Inappropriate style or formatting',
      Other: 'Other',
      SupportedByVerifiedVerdicts: 'Supported by verified verdicts',
      VerdictRetrievalError:
        'Court decision retrieval failed — this answer is based on general knowledge only.'
    },
    de: {
      Page: 'Seite',
      Pages: 'Seiten',
      CopyToClipboard: 'In die Zwischenablage kopieren',
      Copy: 'Kopieren',
      Copied: 'Kopiert!',
      Helpful: 'Als hilfreich markieren',
      NotHelpful: 'Als nicht hilfreich markieren',
      MarkedHelpful: 'Als hilfreich markiert',
      MarkedNotHelpful: 'Als nicht hilfreich markiert',
      SaveResponse: 'Snippet speichern',
      ResponseSaved: 'Snippet gespeichert',
      Saved: 'Gespeichert',
      RatingSaved: 'Bewertung gespeichert. Möchten Sie zusätzliches Feedback geben?',
      WhatDidYouLike: 'Was hat Ihnen an der Antwort gefallen?',
      WhatDidntYouLike: 'Was hat Ihnen an der Antwort nicht gefallen?',
      FeedbackHelpsImprove: 'Ihr Feedback hilft uns, die Qualität der KI-Antworten zu verbessern',
      FeedbackHelpsImproveNegative:
        'Ihr Feedback hilft uns zu verstehen, was schief gelaufen ist und zukünftige Antworten zu verbessern',
      YourFeedback: 'Ihr Feedback',
      HowCouldThisBeImproved: 'Wie könnte diese Antwort verbessert werden?',
      SendFeedback: 'Feedback senden',
      SelectIssue: 'Wählen Sie das Problem aus, das Sie angetroffen haben',
      IncorrectOrOutdatedInformation: 'Falsche oder veraltete Informationen',
      IncompleteResponse: 'Unvollständige Antwort',
      DidNotFollowInstructions: 'Anweisungen nicht befolgt',
      InappropriateStyleOrFormatting: 'Unangemessener Stil oder Formatierung',
      Other: 'Andere',
      SupportedByVerifiedVerdicts: 'Gestützt durch verifizierte Urteile',
      VerdictRetrievalError:
        'Urteilssuche fehlgeschlagen — diese Antwort basiert nur auf allgemeinem Wissen.'
    }
  })

  const { showCard } = useEntityCard()
  const debugPanelRef = inject<Ref<{ showRawMessages?: boolean }> | undefined>('debugPanelRef')
  const showRawMessages = computed(() => debugPanelRef?.value?.showRawMessages || false)

  const streamingText = inject<Ref<string>>('streamingText', ref(''))
  const streamingChunks = inject<Ref<any[]>>('streamingChunks', ref([]))
  const streamingVerdictContext = inject<Ref<any[]>>('streamingVerdictContext', ref([]))

  const isStreaming = computed(() => props.message._isStreaming === true)

  const thinkingContent = computed(() => {
    const content = props.message.content as { thinking?: string } | null
    return content?.thinking?.trim() || null
  })
  const showSaveSnippetDialog = ref(false)
  const responseSaved = ref(false)
  const showFeedbackDialog = ref(false)
  const feedbackComment = ref('')
  const selectedIssue = ref('')
  const feedbackUuid = ref<string | null>(null)
  const feedbackDialogRef = ref<HTMLElement | null>(null)
  const preferenceSuggestionRef = ref<any>(null)
  const rating = ref<'positive' | 'negative' | null>(null)

  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  const entityRegistry = new Map()
  const documentRegistry = new Map()
  const documentRegistryByUuid = new Map<string, DocumentRegistryEntry>()
  const verdictCitationRegistry = new Map<string, VerdictChunk>()
  let registryCounter = 0

  const { verdictChunks } = useVerdictChunks({ message: toRef(props, 'message') })

  const chatType = computed(() => (sessionType === AIChatSessionTypeEnum.CASE ? 'case' : 'global'))
  const messageUuid = computed(() => props.message.uuid ?? '')

  const {
    hoveredCitation,
    showCitationPopover,
    scheduleCitationHide,
    keepCitationOpen,
    citationPopoverStyle
  } = useVerdictCitationPopover({ registry: verdictCitationRegistry, chatType, messageUuid })

  const { pdfErrorUuids, openVerdictPdf, downloadVerdictPdf } = useVerdictPdfActions({
    chatType,
    messageUuid
  })

  const { verdictHintVisible, enableCourtDb, handleReask } = useVerdictHint({
    message: toRef(props, 'message'),
    isLatestMessage: toRef(props, 'isLatestMessage'),
    chatType
  })

  const handleEntityClick = (entity: any) => {
    showCard(entity, event?.target as HTMLElement)
    window.dispatchEvent(new CustomEvent('entity-click', { detail: entity }))
  }

  const handleDocumentClick = (document: any) => {
    emit('document-click', document)
  }

  const renderIcon = (type: 'user' | 'building' | 'book') => {
    const iconBaseClass = 'inline-block h-2.5 w-2.5 me-1 text-purple-600'
    switch (type) {
      case 'user':
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user-icon lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`
      case 'building':
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building-icon lucide-building"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>`
      case 'book':
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-book-open-icon lucide-book-open"><path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/></svg>`
      default:
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6l2 2m0 0l-2 2m2-2H6m12 4H6m6 4H6m6-4H6m6 4H6m6-4H6" /></svg>`
    }
  }

  const sanitizeRenderedMarkdown = (html: string): string =>
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'b',
        'i',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'a',
        'span',
        'button',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'hr',
        'svg',
        'path',
        'circle',
        'rect'
      ],
      ALLOWED_ATTR: [
        'class',
        'href',
        'target',
        'rel',
        'data-verdict-ref',
        'data-ref',
        'viewBox',
        'fill',
        'stroke',
        'd',
        'stroke-width',
        'cx',
        'cy',
        'r'
      ]
    })

  const getCitationBadgeHtml = (num: string, ref: string = num): string =>
    `<button class="verdict-citation-badge" data-verdict-ref="${ref}">${num}</button>`

  const processTextCitations = (text: string): string => {
    if (verdictCitationRegistry.size === 0) return text
    return text.replace(/\[(\d+)\]/g, (match, num) =>
      verdictCitationRegistry.has(num) ? getCitationBadgeHtml(num) : match
    )
  }

  const getEntityHtml = (entity: any) => {
    const iconHtml = renderIcon(entity.is_company ? 'building' : 'user')
    const key = `entity-${registryCounter++}`
    entityRegistry.set(key, entity)
    return `<span class="entity-link inline-flex cursor-pointer items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">${iconHtml}${escapeHtml(entity.name)}</span>`
  }

  const getDocumentHtml = (doc: any) => {
    const key = `doc-${registryCounter++}`
    documentRegistry.set(key, doc)
    if (doc.uuid) documentRegistryByUuid.set(doc.uuid, { ...doc, _key: key })
    const pageText =
      doc.pages.length === 0
        ? ''
        : doc.pages.length === 1
          ? `${t('Page')} ${escapeHtml(String(doc.pages[0]))}`
          : `${t('Pages')}: ${doc.pages.map((p: any) => escapeHtml(String(p))).join(', ')}`
    return renderDocumentBadge(key, doc.title, renderIcon('book'), pageText)
  }

  // The LLM sometimes references documents that are not in the response's
  // chunk list. Synthesise a registry entry on the fly so the anchor still
  // renders as a clickable badge rather than a dead link.
  const synthesiseDocumentEntry = (uuid: string, title: string): DocumentRegistryEntry => {
    const key = `doc-${registryCounter++}`
    const docObj = { uuid, title, pages: [] }
    documentRegistry.set(key, docObj)
    const entry = { ...docObj, _key: key }
    documentRegistryByUuid.set(uuid, entry)
    return entry
  }

  const rewriteAnchors = (html: string) =>
    rewriteDocumentAnchorsAsBadges({
      html,
      registry: documentRegistryByUuid,
      renderIcon: () => renderIcon('book'),
      onMissing: synthesiseDocumentEntry
    })

  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)

  // Scroll to element with smooth animation
  const scrollToElement = (element: HTMLElement) => {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    })
  }

  const submitFeedback = async () => {
    if (feedbackUuid.value && feedbackComment.value.trim()) {
      try {
        // Prepare comment with issue type for negative feedback
        let comment = feedbackComment.value.trim()
        if (rating.value === 'negative' && selectedIssue.value) {
          const issueText = t(selectedIssue.value)
          comment = `Issue: ${issueText}\n\nFeedback: ${comment}`
        }

        // Update existing feedback with comment
        const updatedFeedback = await FeedbackService.feedbackPartialUpdate({
          uuid: feedbackUuid.value,
          requestBody: {
            comment: comment
          }
        })

        // Check if preference suggestion was generated (for negative feedback)
        if (rating.value === 'negative' && (updatedFeedback as any).preference_suggestion) {
          // Create updated message with preference suggestion
          const updatedMessage = {
            ...props.message,
            preference_suggestion: {
              id: `feedback_suggestion_${Date.now()}`,
              title: (updatedFeedback as any).preference_suggestion.title,
              content: (updatedFeedback as any).preference_suggestion.content,
              reasoning: (updatedFeedback as any).preference_suggestion.reasoning,
              suggested_scope: (updatedFeedback as any).preference_suggestion.suggested_scope,
              state: 'PENDING',
              created_preference_uuid: undefined
            }
          }
          emit('update-message', updatedMessage)
        }
      } catch (error) {
        console.error('Failed to update feedback:', error)
      }
    }
    closeFeedbackDialog()
  }

  const closeFeedbackDialog = () => {
    showFeedbackDialog.value = false
    feedbackComment.value = ''
    selectedIssue.value = ''
    // Reset for next rating
    setTimeout(() => {
      rating.value = null
      feedbackUuid.value = null
    }, 100)
  }

  const onRated = ({
    value,
    feedback_uuid
  }: {
    value: 'positive' | 'negative'
    feedback_uuid?: string
  }) => {
    rating.value = value
    feedbackUuid.value = feedback_uuid ?? null
    showFeedbackDialog.value = true
  }

  const openSaveSnippetDialog = () => {
    showSaveSnippetDialog.value = true

    // Listen for the dialog close event to detect if a response was saved
    const handleDialogClose = () => {
      // Check if the response was saved (this would be set by the SaveResponseDialog component)
      // For now, we'll simulate this by always showing the saved state temporarily
      responseSaved.value = true

      // Reset the saved state after a delay
      setTimeout(() => {
        responseSaved.value = false
      }, 2000)
    }

    // Call the handler when the dialog is closed
    setTimeout(() => {
      const dialogCloseListener = () => {
        handleDialogClose()
        window.removeEventListener('save-response-dialog-closed', dialogCloseListener)
      }
      window.addEventListener('save-response-dialog-closed', dialogCloseListener, { once: true })
    }, 100)
  }

  const getMessageContent = () => {
    const messageContentObj = props.message.content as any
    if (!messageContentObj?.chunks) return ''

    const chunks = Array.isArray(messageContentObj?.chunks)
      ? (messageContentObj?.chunks as any[])
      : []

    let raw = ''
    for (const chunk of chunks) {
      if (!chunk || typeof chunk !== 'object' || !('type' in chunk)) continue

      switch (chunk.type) {
        case 'text':
          raw += chunk.text ?? ''
          break
        case 'entity':
          raw += chunk.name ?? ''
          break
        case 'document':
          raw += `${chunk.title} (${chunk.pages.length === 1 ? t('Page') + ' ' + chunk.pages[0] : t('Pages') + ': ' + chunk.pages.join(', ')})`
          break
        default:
          break
      }
    }

    return raw
  }

  const renderedContent = computed(() => {
    entityRegistry.clear()
    documentRegistry.clear()
    documentRegistryByUuid.clear()
    verdictCitationRegistry.clear()
    registryCounter = 0

    if (isStreaming.value) {
      const completedChunks = streamingChunks.value || []
      const liveText = streamingText.value || ''

      if (completedChunks.length === 0 && !liveText)
        return ''

        // Build verdict citation registry from pre-loaded context (keyed by UUID)
      ;(streamingVerdictContext.value as any[]).forEach((v) => {
        verdictCitationRegistry.set(v.court_judgement_uuid, v as VerdictChunk)
      })

      const streamingUuidToNum = new Map<string, number>()
      let raw = ''
      for (const chunk of completedChunks) {
        if (!chunk || typeof chunk !== 'object' || !('type' in chunk)) continue
        switch (chunk.type) {
          case 'text':
            raw += processTextCitations(chunk.text ?? '')
            break
          case 'verdict': {
            const uuid = (chunk as VerdictChunk).court_judgement_uuid
            if (!streamingUuidToNum.has(uuid))
              streamingUuidToNum.set(uuid, streamingUuidToNum.size + 1)
            raw += getCitationBadgeHtml(String(streamingUuidToNum.get(uuid)!), uuid)
            break
          }
          case 'entity':
            raw += getEntityHtml(chunk)
            break
          case 'document':
            raw += getDocumentHtml(chunk)
            break
        }
      }

      if (liveText) {
        raw += processTextCitations(liveText) + '<span class="streaming-cursor"></span>'
      }

      return sanitizeRenderedMarkdown(rewriteAnchors(md.render(raw)))
    }

    const savedContent = props.message.content as any
    if (!savedContent?.chunks) return ''

    const chunks = Array.isArray(savedContent?.chunks) ? (savedContent?.chunks as any[]) : []

    // Build verdict citation registry (keyed by UUID; also by index for backward-compat with saved messages)
    const uuidToNum = new Map<string, number>()
    let verdictIdx = 0
    for (const chunk of chunks) {
      if (chunk?.type === 'verdict') {
        const v = chunk as VerdictChunk
        const uuid = v.court_judgement_uuid
        if (!uuidToNum.has(uuid)) {
          verdictIdx++
          uuidToNum.set(uuid, verdictIdx)
          verdictCitationRegistry.set(String(verdictIdx), v) // backward-compat: old messages have [N] in text
        }
        verdictCitationRegistry.set(uuid, v)
      }
    }

    let raw = ''
    for (const chunk of chunks) {
      if (!chunk || typeof chunk !== 'object' || !('type' in chunk)) continue

      switch (chunk.type) {
        case 'text':
          raw += processTextCitations(chunk.text ?? '')
          break
        case 'verdict': {
          const uuid = (chunk as VerdictChunk).court_judgement_uuid
          const num = uuidToNum.get(uuid)
          if (num !== undefined) raw += getCitationBadgeHtml(String(num), uuid)
          break
        }
        case 'entity':
          raw += getEntityHtml(chunk)
          break
        case 'document':
          raw += getDocumentHtml(chunk)
          break
        default:
          console.error('Unknown chunk type:', chunk.type)
          break
      }
    }

    return sanitizeRenderedMarkdown(rewriteAnchors(md.render(raw)))
  })

  /** Match side-panel KI-Assistent: wave until markdown body is non-empty. */
  const hasVisibleAssistantStreamHtml = computed(() => {
    const html = renderedContent.value || ''
    if (typeof DOMParser === 'undefined') {
      return (
        html
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim().length > 0
      )
    }
    const text = new DOMParser().parseFromString(html, 'text/html').body.textContent || ''
    return text.replace(/\u00a0/g, ' ').trim().length > 0
  })

  const showAssistantThinkingWave = computed(
    () => isAiAssistantAppRoute.value && isStreaming.value && !hasVisibleAssistantStreamHtml.value
  )

  const proseContent = ref<HTMLElement | null>(null)

  const handleContainerMouseOver = (e: Event) => {
    const badge = (e.target as HTMLElement).closest('.verdict-citation-badge')
    if (badge) {
      const ref = badge.getAttribute('data-verdict-ref')
      if (ref) showCitationPopover(ref, badge)
    }
  }

  const handleContainerMouseOut = (e: Event) => {
    const badge = (e.target as HTMLElement).closest('.verdict-citation-badge')
    if (badge) scheduleCitationHide()
  }

  watch(
    () => (props.message.content as any)?.chunks,
    () => {
      nextTick(() => {
        if (proseContent.value) {
          proseContent.value.removeEventListener('click', handleContainerClick)
          proseContent.value.addEventListener('click', handleContainerClick)
          proseContent.value.removeEventListener('mouseover', handleContainerMouseOver)
          proseContent.value.addEventListener('mouseover', handleContainerMouseOver)
          proseContent.value.removeEventListener('mouseout', handleContainerMouseOut)
          proseContent.value.addEventListener('mouseout', handleContainerMouseOut)
        }
      })
    },
    { deep: true }
  )

  // Watch for preference suggestion changes and auto-scroll
  watch(
    () => props.message.preference_suggestion,
    (newSuggestion) => {
      if (newSuggestion) {
        nextTick(() => {
          if (preferenceSuggestionRef.value) {
            // For Vue components, we need to access the $el property
            const element = preferenceSuggestionRef.value.$el || preferenceSuggestionRef.value
            scrollToElement(element)
          }
        })
      }
    }
  )

  const handleContainerClick = (e: Event) => {
    const target = e.target as HTMLElement
    const entityLink = target.closest('.entity-link')
    const documentLink = target.closest('.document-link')
    const citationBadge = target.closest('.verdict-citation-badge')

    if (citationBadge) {
      const ref = citationBadge.getAttribute('data-verdict-ref')
      if (ref) {
        const verdict = verdictCitationRegistry.get(ref)
        if (verdict) openVerdictPdf(verdict.court_judgement_uuid)
      }
    }

    if (entityLink) {
      const ref = entityLink.getAttribute('data-ref')
      if (ref) {
        const entity = entityRegistry.get(ref)
        if (entity) {
          handleEntityClick(entity)
        }
      }
    }

    if (documentLink) {
      const ref = documentLink.getAttribute('data-ref')
      if (ref) {
        const doc = documentRegistry.get(ref)
        if (doc) {
          emit('document-click', doc)
        }
      }
    }
  }

  onMounted(() => {
    if (proseContent.value) {
      proseContent.value.addEventListener('click', handleContainerClick)
      proseContent.value.addEventListener('mouseover', handleContainerMouseOver)
      proseContent.value.addEventListener('mouseout', handleContainerMouseOut)
    }
  })

  onUnmounted(() => {
    if (proseContent.value) {
      proseContent.value.removeEventListener('click', handleContainerClick)
      proseContent.value.removeEventListener('mouseover', handleContainerMouseOver)
      proseContent.value.removeEventListener('mouseout', handleContainerMouseOut)
    }
  })

  const caseStore = useCaseStore()

  // Preference suggestion handlers
  const handleSuggestionDismissed = () => {
    console.log('Preference suggestion dismissed')
  }

  const handleSuggestionSaved = (preference: any) => {
    console.log('Preference suggestion saved:', preference)
    // Could emit an event to parent to refresh preferences or show success message
  }

  const handleViewInLibrary = () => {
    console.log('View in preference library clicked')
    emit('openPreferencesDialog')
  }
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: scale(1);
  }
</style>

<style>
  .streaming-cursor {
    display: inline-block;
    width: 2px;
    height: 1em;
    background-color: rgb(124 58 237); /* tailwind violet-600 */
    margin-left: 1px;
    vertical-align: text-bottom;
    animation: blink 1s step-end infinite;
  }

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }

  .prose :deep(p) {
    margin: 0;
    display: inline;
  }

  .prose :deep(ul),
  .prose :deep(ol) {
    margin: 0;
    padding-left: 1.5em;
  }

  .prose :deep(li) {
    margin: 0;
  }

  .prose :deep(li:not(:last-child)) {
    margin-bottom: 0.25em;
  }
</style>

<style>
  .verdict-citation-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 9999px;
    background-color: rgb(124 58 237);
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    vertical-align: middle;
    margin: 0 1px;
    border: none;
    padding: 0;
    transition: background-color 150ms;
    flex-shrink: 0;
  }

  .verdict-citation-badge:hover {
    background-color: rgb(109 40 217);
  }
</style>

<style scoped>
  :deep(.vjs-tree) {
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
      monospace !important;
    font-size: 0.75rem !important;
    line-height: 1rem !important;
    background-color: transparent !important;
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__string) {
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__number) {
    color: rgb(129 140 248) !important;
  }

  :deep(.vjs-tree .vjs-value__boolean) {
    color: rgb(244 114 182) !important;
  }

  :deep(.vjs-tree .vjs-value__null) {
    color: rgb(248 113 113) !important;
  }

  :deep(.vjs-tree .vjs-key) {
    color: rgb(139 92 246) !important;
  }
</style>
